const API_KEY = process.env.REACT_APP_GROQ_API_KEY;

export async function generateQuestionsFromSchema(tableSchemas) {
  const tableNames = Object.keys(tableSchemas);
  const tableCount = tableNames.length;
  const perTable = 5;
  const joinCount = tableCount > 1 ? 5 : 0;
  const total = tableCount * perTable + joinCount;

  const schemaText = Object.entries(tableSchemas)
    .map(([table, cols]) => {
      const colDefs = cols.map((c) => `  ${c.name} ${c.type}`).join("\n");
      return `Table: ${table}\n${colDefs}`;
    })
    .join("\n\n");

  const prompt = `You are a SQL instructor. Given the following database schema, generate exactly ${total} UNIQUE SQL practice questions (variation seed: ${Math.random().toString(36).slice(2)}):
- ${perTable} questions per table (each question must query ONLY that single table, no joins)
${tableCount > 1 ? `- ${joinCount} questions that use JOINs across multiple tables` : ''}
- Every question must have a different SQL answer — no duplicate queries allowed

Schema:
${schemaText}

Return ONLY a valid JSON array with no markdown, no explanation. Each item must have:
- "id": number (1 to ${total})
- "question": string (the question prompt for the student)
- "answer": string (the correct SQL query)
- "mark": number (1-7 based on difficulty)
- "max_attempts": number (4-7 based on difficulty)
- "orderMatters": boolean (true if ORDER BY is required)
- "aliasStrict": boolean (true if column aliases are required)
- "tables": array of table names used in the answer (e.g. ["Employees"] or ["Employees","Departments"])

Example format:
 [{"id":1,"question":"...","answer":"SELECT ...","mark":2,"orderMatters":false,"aliasStrict":false,"max_attempts":3,"tables":["Employees"]}]`

  const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: "llama-3.3-70b-versatile",
      messages: [{ role: "user", content: prompt }],
      max_tokens: 4096,
      temperature: 1.0,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = res.status === 429
      ? "AI question generation is temporarily unavailable due to usage limits. Please try again in a few minutes."
      : `AI unavailable (${res.status}): ${err?.error?.message || res.statusText}`;
    throw new Error(msg);
  }

  const data = await res.json();
  const raw = data.choices[0].message.content ?? "";
  const cleaned = raw.replace(/```json|```/g, "").trim();
  try {
    return JSON.parse(cleaned);
  } catch (e){
    const lastComplete = cleaned.lastIndexOf('},');
    if (lastComplete === -1) throw new Error("Could not parse AI response",e.message);
    return JSON.parse(cleaned.slice(0, lastComplete + 1) + ']');
  }
}

/**
 * Filters presets based on whether the tables in the preset match the selected tables.
 */
export const filteredPresets = (questionId, selectedTable, presets) => {
  const tables = selectedTable[questionId] || [];

  return tables.length > 0
    ? presets.filter(p => {
        if (!Array.isArray(p.tables) || p.tables.length !== tables.length) {
          return false;
        }
        const sortedP = [...p.tables].sort();
        const sortedSelected = [...tables].sort();
        return sortedP.every((val, index) => val === sortedSelected[index]);
      })
    : presets;
};
